import React from 'react';
export function OperationCostsSection() {
  return <section data-id="element-3240">
      <div className="bg-[#1874cd] py-4 px-6 rounded-t-xl" data-id="element-3241">
        <h2 className="text-2xl md:text-3xl font-bold text-white" data-id="element-3242">
          2. Operation Costs
        </h2>
      </div>
      <div className="bg-white p-8 rounded-b-xl shadow-sm border border-gray-200 border-t-0 space-y-12" data-id="element-3243">
        <p className="text-gray-700 text-lg leading-relaxed" data-id="element-3244">
          These costs come from the accumulated hours of cutting metal on the
          machining center. Estimating operation costs requires carefully
          looking at how the machine's design and construction might impact its
          productivity.
        </p>

        {/* Cycle Time */}
        <div className="space-y-6 pt-6 border-t border-gray-100" data-id="element-3245">
          <h3 className="text-2xl font-bold text-[#1874cd] italic" data-id="element-3246">
            Cycle Time -
          </h3>
          <div className="grid md:grid-cols-2 gap-8 items-start" data-id="element-3247">
            <div className="text-gray-700 text-lg leading-relaxed space-y-6" data-id="element-3248">
              <p data-id="element-3249">
                Cycle time is an important component of determining lifecycle
                ROI for a machine. The authors cite the following machine
                features as examples of design elements that can affect cycle
                times and hence ROI:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2" data-id="element-3250">
                <li data-id="element-3251">A high-power spindle can speed cutting</li>
                <li data-id="element-3252">
                  A larger spindle bearing can better handle cutting forces for
                  greater accuracy and hence less finishing work
                </li>
                <li data-id="element-3253">
                  Acceleration/deceleration rates can affect non-cutting time
                </li>
                <li data-id="element-3254">
                  Structural characteristics such as single-piece casting,
                  three-point leveling, and a tiered or stepped column design
                  can reduce machine vibration for greater accuracy
                </li>
              </ul>
            </div>
            <div className="space-y-6 text-center" data-id="element-3255">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/Mikron%20Mill%20S%20Machine%20Cutaway.jpg" alt="Mikron Mill S Machine Cutaway" className="block mx-auto w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3256" />
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/Step-Tec%20Spindle.jpg" alt="Step-Tec Spindle" className="block mx-auto w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3257" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mt-8" data-id="element-3258">
            <div className="text-gray-700 text-lg leading-relaxed" data-id="element-3259">
              <p data-id="element-3260">
                Another thing to remember when thinking about cycle time is part
                setup. Can multiple workpieces be prepared in a single setup?
                Can the machine be preprogrammed for each job? Cranking out more
                parts with less labor automatically lowers the cost per part.
              </p>
            </div>
            <div data-id="element-3261">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/MILL%20S/High%20Speed%20Machining%20Flexibility%203.jpg" alt="High Speed Machining Flexibility" className="block w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3262" />
            </div>
          </div>
        </div>

        {/* Tool Life */}
        <div className="space-y-6 pt-8 border-t border-gray-100" data-id="element-3263">
          <h3 className="text-2xl font-bold text-[#1874cd] italic" data-id="element-3264">
            Tool Life -
          </h3>
          <div className="grid md:grid-cols-2 gap-8 items-start" data-id="element-3265">
            <div className="text-gray-700 text-lg leading-relaxed space-y-6" data-id="element-3266">
              <p data-id="element-3267">
                Operation costs should also take account of tool life. As noted
                above, the way the machine design handles cutting forces and
                maintains stability ultimately has effects on ROI. The authors
                note the benefits of rigid construction and extended tool life:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2" data-id="element-3268">
                <li data-id="element-3269">Fewer necessary cutting passes reduce processing times</li>
                <li data-id="element-3270">
                  Less rotational deflection due to stability issues leaves less
                  stock for the finish tools
                </li>
                <li data-id="element-3271">
                  Fewer passes from finish tools are necessary for certain
                  features
                </li>
                <li data-id="element-3272">
                  Increased metal removal rates are realized from cutting at
                  deeper axial depths
                </li>
              </ul>
              <p data-id="element-3273">
                In addition, longer tool life means less labor devoted to
                changing tools, measuring them, updating tool information and
                other associated tasks.
              </p>
            </div>
            <div className="space-y-6 text-center" data-id="element-3274">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/High%20Acceleration%203.jpg" alt="High Acceleration" className="block mx-auto w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3275" />
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/Benefit%20of%205%20axis%20machining%202.jpg" alt="Benefit of 5 axis machining" className="block mx-auto w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3276" />
            </div>
          </div>
        </div>

        {/* Part Quality */}
        <div className="space-y-6 pt-8 border-t border-gray-100" data-id="element-3277">
          <h3 className="text-2xl font-bold text-[#1874cd] italic" data-id="element-3278">
            Part Quality -
          </h3>
          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-3279">
            <div className="text-gray-700 text-lg leading-relaxed" data-id="element-3280">
              <p data-id="element-3281">
                Rigid, accurate machining directly affects a third category of
                operational costs: part quality. Parts machined to tighter
                tolerances and higher-quality surface finishes are less likely
                to require secondary operations like spotting, hand finishing or
                EDM processes. Reducing extra processing obviously has an impact
                on turnaround and lead times.
              </p>
            </div>
            <div data-id="element-3282">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/MILL%20S/High%20Speed%20Machining%20Process%20Stability%201.jpg" alt="High Speed Machining Process Stability" className="block w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3283" />
            </div>
          </div>
        </div>

        {/* Process Reliability */}
        <div className="space-y-6 pt-8 border-t border-gray-100" data-id="element-3284">
          <h3 className="text-2xl font-bold text-[#1874cd] italic" data-id="element-3285">
            Process Reliability -
          </h3>
          <div className="grid md:grid-cols-2 gap-8 items-start" data-id="element-3286">
            <div className="text-gray-700 text-lg leading-relaxed" data-id="element-3287">
              <p data-id="element-3288">
                ROI can be affected by the amount and type of labor involved
                over the course of a machining cycle. Control and programming
                features that enable uninterrupted or unattended machining can
                bump up return because of less need for operator intervention
                and thus fewer chances for the introduction of mistakes or
                variation.
              </p>
            </div>
            <div className="space-y-6 text-center" data-id="element-3289">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/MILL%20S/High%20Speed%20Machining%20Process%20Stability%202.jpg" alt="High Speed Machining Process Stability" className="block mx-auto w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3290" />
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.peakedm.com/uploaded/images/Mikron/Milling%202019/MILL%20S/High%20Speed%20Machining%20Process%20Stability%204.jpg" alt="High Speed Machining Process Stability" className="block mx-auto w-full h-auto rounded-lg shadow-sm border border-gray-200" data-id="element-3291" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}